import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
  TouchApp as ClickIcon,
  Block as BounceIcon,
  Cancel as UnsubscribeIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { sanitizeHtml } from '../../../utils/safeHtml';

function CampaignDetail({
  campaign, onEdit, onDelete, onDuplicate,
}) {
  const theme = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (campaign) {
      loadAnalytics();
      loadRecipients();
    }
  }, [campaign]);

  const loadAnalytics = async () => {
    try {
      const response = await fetch(`/api/v1/campaigns/${campaign._id}/analytics`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadRecipients = async () => {
    try {
      const response = await fetch(`/api/v1/campaigns/${campaign._id}/recipients?limit=10`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setRecipients(data.recipients || []);
      }
    } catch (error) {
      console.error('Error loading recipients:', error);
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(false);
    onDelete(campaign._id);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'default';
      case 'scheduled': return 'info';
      case 'paused': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckIcon />;
      case 'draft': return <InfoIcon />;
      case 'scheduled': return <ScheduleIcon />;
      case 'paused': return <WarningIcon />;
      case 'completed': return <CheckIcon />;
      case 'cancelled': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <EmailIcon />;
      case 'sms': return <CampaignIcon />;
      case 'social_media': return <CampaignIcon />;
      case 'direct_mail': return <CampaignIcon />;
      case 'multi_channel': return <CampaignIcon />;
      default: return <CampaignIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case 'Excellent': return 'success';
      case 'Good': return 'info';
      case 'Average': return 'warning';
      case 'Poor': return 'error';
      default: return 'default';
    }
  };

  if (!campaign) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a campaign to view details
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Avatar sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            width: 48,
            height: 48,
          }}
          >
            {getTypeIcon(campaign.type)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              {campaign.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {campaign.description}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Edit Campaign">
              <IconButton onClick={() => onEdit(campaign)} color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate Campaign">
              <IconButton onClick={() => onDuplicate(campaign._id)} color="info">
                <DuplicateIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Campaign">
              <IconButton onClick={() => setDeleteDialogOpen(true)} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={campaign.status}
            color={getStatusColor(campaign.status)}
            icon={getStatusIcon(campaign.status)}
            variant="outlined"
          />
          <Chip
            label={campaign.type.replace('_', ' ').toUpperCase()}
            variant="outlined"
            size="small"
          />
          <Chip
            label={campaign.category}
            variant="outlined"
            size="small"
          />
          {campaign.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              variant="outlined"
              size="small"
            />
          ))}
        </Stack>
      </Box>

      {/* Analytics Overview */}
      {analytics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <EmailIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                      {analytics.sent?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Emails Sent
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <ViewIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color="success.main">
                      {analytics.openRate?.toFixed(1) || 0}
                      %
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Open Rate
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <ClickIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color="info.main">
                      {analytics.clickRate?.toFixed(1) || 0}
                      %
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click Rate
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <TrendingIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color="warning.main">
                      {analytics.performance || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Performance
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Campaign Details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Campaign Information" />
            <CardContent>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CampaignIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Campaign Type"
                    secondary={campaign.type.replace('_', ' ').toUpperCase()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PeopleIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Target Recipients"
                    secondary={`${campaign.targetAudience.actualRecipients || campaign.targetAudience.estimatedRecipients || 0} contacts`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Created"
                    secondary={formatDate(campaign.createdAt)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TimeIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Last Sent"
                    secondary={formatDate(campaign.lastSentAt)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Timezone"
                    secondary={campaign.schedule.timezone}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Schedule Information" />
            <CardContent>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Schedule Type"
                    secondary={campaign.schedule.type.replace('_', ' ').toUpperCase()}
                  />
                </ListItem>
                {campaign.schedule.type === 'scheduled' && (
                  <ListItem>
                    <ListItemIcon>
                      <CalendarIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Scheduled For"
                      secondary={formatDate(campaign.schedule.scheduledAt)}
                    />
                  </ListItem>
                )}
                {campaign.schedule.type === 'recurring' && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <TimeIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Frequency"
                        secondary={`${campaign.schedule.recurring.frequency} (every ${campaign.schedule.recurring.interval})`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="End Date"
                        secondary={formatDate(campaign.schedule.recurring.endDate)}
                      />
                    </ListItem>
                  </>
                )}
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sender"
                    secondary={`${campaign.settings.senderName} <${campaign.settings.senderEmail}>`}
                  />
                </ListItem>
                {campaign.settings.replyToEmail && (
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Reply-To"
                      secondary={campaign.settings.replyToEmail}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Template Preview */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Email Template" />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Subject:
                  {' '}
                  {campaign.template.subject}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'background.paper',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(campaign.template.htmlContent) }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Recipients */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Recent Recipients" />
            <CardContent>
              {recipients.length > 0 ? (
                <List dense>
                  {recipients.map((recipient, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {recipient.contact?.name?.charAt(0) || recipient.email.charAt(0)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={recipient.contact?.name || recipient.email}
                        secondary={(
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={recipient.status}
                              size="small"
                              color={getStatusColor(recipient.status)}
                            />
                            {recipient.sentAt && (
                              <Typography variant="caption" color="text.secondary">
                                Sent:
                                {' '}
                                {formatDate(recipient.sentAt)}
                              </Typography>
                            )}
                          </Stack>
                        )}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No recipients yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "
            {campaign.name}
            "? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CampaignDetail;
